import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import ManageSearchRoundedIcon from "@mui/icons-material/ManageSearchRounded";
import Tooltip from "@mui/material/Tooltip";
import TouchAppRoundedIcon from "@mui/icons-material/TouchAppRounded";
import { brand, radius } from "../../theme/businessFlowsTheme";
import { useViewConfig } from "./ViewConfigContext";
import { attributeLabel, displayValue, typeColour, typeLabel } from "../../utils/viewConfig";

// Whether a value is worth a row. An attribute nobody maintained is not shown
// at all - no placeholder, no "Empty": the panel says what an object has, and
// says nothing where it has nothing (product owner, 28 Sep 2026).
const isFilled = (value) => {
  if (value === null || value === undefined || value === false) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return String(value).trim() !== "";
};

// Attributes arrive as { success, key: value | { nestedKey: value } }.
//
// A refusal arrives in the same shape (`success: false`, and the server's own
// `msg`, `status` and `reason` beside it), and reading one as attributes is
// exactly what once showed a reader "Status 404" where an object's properties
// belong. It is not attributes, so it is never rows.
const flattenAttributes = (attributes) => {
  if (!attributes || typeof attributes !== "object" || Array.isArray(attributes)) return [];
  if (attributes.success === false) return [];
  const { success, ...content } = attributes;
  return Object.entries(content)
    .flatMap(([key, value]) => {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        return Object.entries(value).map(([nestedKey, nestedValue]) => ({
          label: attributeLabel(nestedKey),
          value: nestedValue,
        }));
      }
      return [{ label: attributeLabel(key), value: Array.isArray(value) ? value.join(", ") : value }];
    })
    .filter((row) => isFilled(row.value));
};

// The two places what is on screen can have come from. A reader who clicks a
// step of an imported model is reading the file itself, and should be able to
// tell that from an object the library holds - in one quiet line, not a mark
// against every row.
const FROM_LIBRARY = "From the library";
const FROM_DIAGRAM = "From the diagram";

function AttributeValue({ value }) {
  if (value === null || value === undefined || value === "") {
    return <Box component="span" sx={{ color: "text.disabled" }}>Empty</Box>;
  }
  const text = typeof value === "object" ? JSON.stringify(value) : String(value);
  if (/^https?:\/\//.test(text)) {
    return (
      <Box component="a" href={text} target="_blank" rel="noreferrer" sx={{ color: "info.main", fontWeight: 500 }}>
        Open link
      </Box>
    );
  }
  return text;
}

function LinkRow({ entry, icon, label, onOpen, config }) {
  return (
    <ButtonBase
      onClick={() => onOpen(entry)}
      sx={{
        justifyContent: "flex-start",
        textAlign: "left",
        gap: 1.25,
        p: 1.25,
        borderRadius: radius.control,
        border: `1px solid ${brand.line}`,
        "&:hover": { borderColor: brand.inkSoft, bgcolor: brand.canvas },
        "&:focus-visible": { outline: `2px solid ${brand.ink}` },
      }}
    >
      <Box sx={{ color: brand.signalDeep, display: "grid", placeItems: "center" }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
          {label}
        </Typography>
        {entry.Type && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: typeColour(config, entry.Type) }} />
            <Typography variant="caption" color="text.secondary">
              {typeLabel(config, entry.Type)}
            </Typography>
          </Stack>
        )}
      </Box>
    </ButtonBase>
  );
}

function LinkList({ items, icon, emptyText, onOpen, config }) {
  if (!items?.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2.5 }}>
        {emptyText}
      </Typography>
    );
  }
  return (
    <Stack spacing={0.75} sx={{ p: 1.5 }}>
      {items.map((entry, i) => (
        <LinkRow
          key={`${entry.GUID}-${i}`}
          entry={entry}
          icon={icon}
          label={entry.name || typeLabel(config, entry.Type)}
          onOpen={onOpen}
          config={config}
        />
      ))}
    </Stack>
  );
}

// An occurrence is an object whose stored diagram names this one (product
// owner, 28 Sep 2026). The row already names that object - what the reader
// needs is *which* diagram: its BPMN, its SVG, its Mermaid source.
// `_occurrence` and `_occurrenceFormatLabel` say that in the database's own
// words (never a hard-coded format name), so the caption is built from them,
// not guessed from the row. A database whose admin switched
// `occurrence.fromMaps` back on also gets rows for a map that draws this
// object, and those say "In the map of ...".
function occurrenceCaption(entry, config) {
  const name = entry.name || typeLabel(config, entry.Type);
  if (entry._occurrence === "map") return { prefix: "In the map of ", name };
  return { prefix: `In the ${entry._occurrenceFormatLabel || "diagram"} of `, name };
}

// One group per stored format ("BPMN", "SVG", ...) using this database's own
// word for it, so a reader can tell one kind of diagram from another at a
// glance instead of reading a flat list of names. Where an admin switched the
// map half back on, its rows are one more group, first.
function groupOccurrences(items) {
  const groups = [];
  const maps = items.filter((entry) => entry._occurrence === "map");
  if (maps.length) groups.push({ key: "map", heading: "On maps", items: maps });

  const byFormat = new Map();
  items
    .filter((entry) => entry._occurrence !== "map")
    .forEach((entry) => {
      const label = entry._occurrenceFormatLabel || "Diagram";
      if (!byFormat.has(label)) byFormat.set(label, []);
      byFormat.get(label).push(entry);
    });
  byFormat.forEach((rows, label) => groups.push({ key: label, heading: label, items: rows }));

  return groups;
}

function OccurrenceList({ items, searched, icon, emptyText, onOpen, config }) {
  if (!items?.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2.5 }}>
        {emptyText}
      </Typography>
    );
  }
  // Only once the server has actually said the search was partial - never
  // before `searched` has arrived, and never for a search that ran clean.
  const partial = Boolean(searched) && searched.complete === false;
  return (
    <Box sx={{ p: 1.5 }}>
      <Stack spacing={1.75}>
        {groupOccurrences(items).map((group) => (
          <Box key={group.key}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block", px: 0.25, pb: 0.5, fontWeight: 700, letterSpacing: 0.5, lineHeight: 1.4 }}
            >
              {group.heading}
            </Typography>
            <Stack spacing={0.75}>
              {group.items.map((entry, i) => {
                const { prefix, name } = occurrenceCaption(entry, config);
                return (
                  <LinkRow
                    key={`${entry.GUID}-${i}`}
                    entry={entry}
                    icon={icon}
                    label={
                      <>
                        {prefix}
                        <Box component="span" sx={{ fontStyle: "italic" }}>
                          {name}
                        </Box>
                      </>
                    }
                    onOpen={onOpen}
                    config={config}
                  />
                );
              })}
            </Stack>
          </Box>
        ))}
      </Stack>
      {partial && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", pt: 1.25, px: 0.25 }}>
          Some large diagrams weren't searched.
        </Typography>
      )}
    </Box>
  );
}

// Attributes, occurrences and relationships of one object. Used by advanced
// search and the process viewer.
export default function DetailsPanel({
  selected,
  details,
  path,
  onClose,
  heading,
  emptyText,
  onOpenOccurrence,
  onOpenRelationship,
  onFindInSearch,
  onOpenExplorer,
  onOpenDiagram,
}) {
  const history = useHistory();
  const config = useViewConfig();
  const [tab, setTab] = useState(0);

  if (!selected) {
    return (
      <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ height: "100%", p: 4, textAlign: "center" }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: radius.card,
            display: "grid",
            placeItems: "center",
            bgcolor: brand.mark,
            color: brand.ink,
          }}
        >
          <TouchAppRoundedIcon />
        </Box>
        <Typography sx={{ fontWeight: 650 }}>No result selected</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 260 }}>
          {emptyText || "Choose Details on a result to see its attributes, where it occurs and what it's related to."}
        </Typography>
      </Stack>
    );
  }

  const stored = flattenAttributes(details.attributes);
  // What the drawing itself says about what is selected, where the caller
  // reads a drawing at all. An array - even an empty one - is what says so.
  const drawn = Array.isArray(details.properties) ? details.properties.filter((row) => isFilled(row.value)) : null;
  const sections = [
    { key: "stored", heading: FROM_LIBRARY, rows: stored },
    { key: "drawn", heading: FROM_DIAGRAM, rows: drawn || [] },
  ].filter((section) => section.rows.length);
  const attributes = sections.flatMap((section) => section.rows);
  const counts = [attributes.length, details.occurrences?.length || 0, details.relationships?.length || 0];
  // An object whose details couldn't be read says so, in the server's own
  // words. Without this the panel stayed on its skeletons for ever. Where the
  // drawing itself has something to say, that is shown instead - a reader
  // clicking a step of an imported model is not looking at a failure.
  const failed = Boolean(details.error) && !attributes.length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ px: 2.5, pt: 2, pb: 1 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {(heading || selected.Type) && (
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {heading || typeLabel(config, selected.Type)}
            </Typography>
          )}
          <Typography component="h2" sx={{ fontWeight: 700, fontSize: "1.05rem", lineHeight: 1.3, overflowWrap: "anywhere" }}>
            {selected.name}
          </Typography>
          {/* How the reader reached this object, where they reached it by
              following a relationship: the two objects with the relationship
              between them, written in the direction it actually runs. A
              caller with no relationship to report (advanced search) sends
              none and the line isn't there. */}
          {selected.connection && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, overflowWrap: "anywhere" }}>
              {selected.connection}
            </Typography>
          )}
          {/* What that relationship holds, where it holds anything - beside
              the sentence and never among the object's own attributes, which
              are a different thing about a different node. */}
          {(selected.connectionAttributes || []).map((row) => (
            <Typography key={row.key} variant="caption" color="text.secondary" sx={{ display: "block", overflowWrap: "anywhere" }}>
              {attributeLabel(row.key)}: <Box component="span" sx={{ fontWeight: 600 }}>{displayValue(row.value)}</Box>
            </Typography>
          ))}
        </Box>
        {/* The same three ways of looking at an object, wherever this panel
            appears: its diagram, its relationships, and advanced search. */}
        {onOpenDiagram && (
          <Tooltip title="Open diagram">
            <IconButton size="small" onClick={() => onOpenDiagram(selected)} aria-label={`Open the diagram of ${selected.name}`}>
              <AccountTreeRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onOpenExplorer && (
          <Tooltip title="Open in Relationship Explorer">
            <IconButton size="small" onClick={() => onOpenExplorer(selected)} aria-label={`Open ${selected.name} in the Relationship Explorer`}>
              <HubRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onFindInSearch && (
          <Tooltip title="Find in Advanced search">
            <IconButton size="small" onClick={() => onFindInSearch(selected)} aria-label={`Find ${selected.name} in Advanced search`}>
              <ManageSearchRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onClose && (
          <IconButton size="small" onClick={onClose} aria-label="Close details">
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        variant="fullWidth"
        sx={{
          px: 1.5,
          minHeight: 40,
          borderBottom: `1px solid ${brand.line}`,
          "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 600, fontSize: "0.85rem", px: 1 },
          "& .MuiTabs-indicator": { bgcolor: brand.ink, height: 3, borderRadius: 3 },
          "& .Mui-selected": { color: `${brand.ink} !important` },
        }}
      >
        {["Attributes", "Occurrences", "Relationships"].map((label, i) => (
          <Tab key={label} label={details.loading || failed ? label : `${label} (${counts[i]})`} />
        ))}
      </Tabs>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {failed ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2.5 }} role="status">
            {details.error}
          </Typography>
        ) : details.loading ? (
          <Stack spacing={1.5} sx={{ p: 2.5 }}>
            {[0, 1, 2, 3].map((i) => (
              <Box key={i}>
                <Skeleton width="35%" height={16} />
                <Skeleton width="85%" height={20} />
              </Box>
            ))}
          </Stack>
        ) : tab === 0 ? (
          attributes.length ? (
            <Box sx={{ px: 2.5, py: 1 }}>
              {sections.map((section, s) => (
                <Box key={section.key} sx={{ pt: s ? 2 : 0 }}>
                  {/* Where both have something to say, each says which it is;
                      where only the drawing does, one quiet line still says so. */}
                  {(sections.length > 1 || (drawn && section.key === "drawn")) && (
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ display: "block", pb: 0.25, fontWeight: 700, letterSpacing: 0.5, lineHeight: 1.6 }}
                    >
                      {section.heading}
                    </Typography>
                  )}
                  <Box component="dl" sx={{ m: 0 }}>
                    {section.rows.map(({ label, value }, i) => (
                      <Box key={`${label}-${i}`} sx={{ py: 1.25, borderTop: i ? `1px solid ${brand.line}` : 0 }}>
                        <Typography component="dt" variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          {label}
                        </Typography>
                        <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: "anywhere", whiteSpace: "pre-line" }}>
                          <AttributeValue value={value} />
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2.5 }}>
              {drawn ? "Nothing is maintained for this element." : "This object has no attributes."}
            </Typography>
          )
        ) : tab === 1 ? (
          <OccurrenceList
            items={details.occurrences}
            searched={details.occurrencesSearched}
            icon={<AccountTreeRoundedIcon fontSize="small" />}
            emptyText="This object isn't drawn in any diagram yet."
            onOpen={onOpenOccurrence || ((occ) => history.push(`${path}/map/${occ.GUID}`))}
            config={config}
          />
        ) : (
          <LinkList
            items={details.relationships}
            icon={<HubRoundedIcon fontSize="small" />}
            emptyText="This object has no relationships."
            onOpen={onOpenRelationship || ((rel) => history.push(`${path}/object/${rel.GUID}`))}
            config={config}
          />
        )}
      </Box>
    </Box>
  );
}
